import ko from "knockout";
import CategoryTagView from "viewModels/CategoryTagView";
import ViewModel from "viewModels/ViewModel";
import loggingService from "services/loggingService";
import speechAndTextAnalyticsService from "services/speechAndTextAnalyticsService";
import authService from "services/authService";

const CategoriesView = ViewModel.extend(function CategoriesView(
    conversationId,
    isFeatureEnabled
) {
    const DEFAULT_PAGE_SIZE = 25;
    const STA_DATA_VIEW_PERMISSION = "perm:speechAndTextAnalytics:data:view";
    const STA_CATEGORY_VIEW_PERMISSION =
        "perm:speechAndTextAnalytics:category:view";

    const self = this;

    self.hasDataPermission = ko.observable(
        authService.hasAuthorization(STA_DATA_VIEW_PERMISSION)
    );
    self.useCategorizerResults = ko.observable(
        !authService.hasAuthorization(STA_CATEGORY_VIEW_PERMISSION)
    );

    self.categoryTagViews = ko.observableArray([]);
    self.orgCategories = ko.observableArray([]);
    self.currentBatchFailed = ko.observable(false);
    self.categorizerResponse = ko.observable();
    self.pageNumber = ko.observable(1);
    self.isLastPage = ko.observable(false);
    self.noResults = ko.observable(false);
    self.categoriesLoaded = ko.observable(false);

    // compare category name, description, interactionType, criteria
    const isCategoryChanged = function (category, entity) {
        // interactionType and criteria fields are currently considered optional from categorizer response
        return (
            category.name !== entity.name ||
            category.description !== entity.description ||
            (entity.interactionType &&
                category.interactionType !== entity.interactionType) ||
            (entity.criteria &&
                JSON.stringify(category.criteria) !==
                    JSON.stringify(entity.criteria))
        );
    };

    // create a tag for each category
    self.addCategoryTagViews = function () {
        // isLastPage=true disables the "See more" button
        self.isLastPage(
            self.categorizerResponse().pageCount <= self.pageNumber()
        );
        const newViews = [];
        // find category in orgCategories by ID, then create a new subView if necessary
        self.categorizerResponse().entities.forEach((entity) => {
            // if no category view permission OR if a call to category definitions fails, use data from categorizer
            const category =
                self.useCategorizerResults() || self.currentBatchFailed()
                    ? entity
                    : self.orgCategories().find((c) => c.id === entity.id);
            if (category && category.name) {
                let displayLink = true;
                if (self.useCategorizerResults() || self.currentBatchFailed()) {
                    displayLink = false;
                }
                const categoryTagView = self.subView(
                    CategoryTagView,
                    category,
                    isCategoryChanged(category, entity),
                    displayLink
                );
                newViews.push(categoryTagView);
            }
        });
        // if no new views created, check if any results at all
        if (newViews.length === 0) {
            if (self.isLastPage()) {
                self.noResults(self.categoryTagViews().length === 0);
            } else {
                self.pageNumber(self.pageNumber() + 1);
            }
        } else {
            self.categoryTagViews.push(...newViews);
            self.categoryTagViews.sort((a, b) =>
                a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
            );
        }
    };

    self.loadMore = function () {
        self.pageNumber(self.pageNumber() + 1);
        self.categoriesLoaded(false);
    };

    // Get detected categories and their matching current categories
    self.getCategoriesResponses = function () {
        speechAndTextAnalyticsService
            .getConversationCategories(
                conversationId(),
                self.pageNumber(),
                DEFAULT_PAGE_SIZE
            )
            .then(async (categorizerResponse) => {
                self.categorizerResponse(categorizerResponse);
                self.noResults(
                    self.categorizerResponse().entities.length === 0 &&
                        self.pageNumber() === 1
                );
                if (!self.noResults()) {
                    // No detected categories
                    if (!self.useCategorizerResults()) {
                        await self.batchGetOrgCategories();
                    }
                    self.addCategoryTagViews();
                }
            })
            .catch((err) => {
                loggingService.error(
                    "Unexpected error when fetching Categorizer response.",
                    err
                );
                if (self.pageNumber() === 1) {
                    self.noResults(true);
                } else {
                    self.isLastPage(true);
                }
            })
            .finally(() => {
                self.categoriesLoaded(true);
            });
    };

    self.computed(function () {
        if (isFeatureEnabled && self.hasDataPermission()) {
            self.getCategoriesResponses();
        }
    });

    // Get current categories from Category Definitions service with detected category ids from Categorizer service
    self.batchGetOrgCategories = async function () {
        self.currentBatchFailed(false);
        try {
            const categorizerObjects = self.categorizerResponse().entities;
            const requestedIds = categorizerObjects.map(
                (category) => category.id
            );
            const requestedIdsParam = requestedIds.join(",");

            const response =
                await speechAndTextAnalyticsService.batchGetCategories(
                    requestedIdsParam
                );
            self.orgCategories().push(...response.entities);
        } catch (err) {
            loggingService.error(
                "Unexpected error when fetching Category Definitions batch GET response.",
                err
            );
            self.currentBatchFailed(true);
        }
    };
});

export default CategoriesView;
